import { take, filter } from 'rxjs/operators';
import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { environment } from '../../../environments/environment';
import * as actions from '../../store/actions';
import * as selectors from '../../store/reducers';

@Component({
  selector: 'ca-photo-picker',
  templateUrl: 'photo-picker.component.html',
  styleUrls: ['photo-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhotoPickerItemComponent),
      multi: true
    }
  ]
})
export class PhotoPickerItemComponent implements ControlValueAccessor, OnDestroy {
  @ViewChild('fileInput')
  file;

  @Input()
  queryString: string;
  @Input()
  title: string;
  hover: boolean;
  progress: number;
  photo: string;

  private id: string;
  private uploadProgressSub: Subscription;
  private uploadSuccessSub: Subscription;

  constructor(public store: Store<any>, public cd: ChangeDetectorRef) {}

  changePhoto() {
    this.file.nativeElement.click();
  }

  clearFileInput() {
    this.file.nativeElement.value = '';
  }

  ngOnDestroy() {
    this.clearUploadSubs();
    if (this.id) {
      this.store.dispatch(new actions.RequestFileUploadCancelAction({ id: this.id }));
    }
  }

  clearUploadSubs() {
    if (this.uploadProgressSub) {
      this.uploadProgressSub.unsubscribe();
    }
    if (this.uploadSuccessSub) {
      this.uploadSuccessSub.unsubscribe();
    }
  }

  photoChanged(file: File) {
    if (this.id) {
      this.store.dispatch(new actions.RequestFileUploadCancelAction({ id: this.id }));
    }

    this.clearUploadSubs();

    this.id = Math.random().toString(36);
    this.store.dispatch(
      new actions.RequestFileUploadStartAction({
        id: this.id,
        url: 'upload',
        file
      })
    );

    this.uploadProgressSub = this.store
      .select(selectors.getFileUploadProgress(this.id))
      .subscribe(progress => {
        this.progress = progress;
        this.cd.detectChanges();
      });

    this.uploadSuccessSub = this.store
      .select(selectors.getFileUploadResponse(this.id))
      .pipe(
        filter(res => !!res && !!res.id),
        take(1)
      )
      .subscribe(success => {
        this.hover = false;
        this.id = null;
        this.progress = 100;
        this.onChange(success);
        this.clearUploadSubs();
        this.cd.detectChanges();
      });
  }

  processWebImage(event) {
    if (!event.target.files.length) {
      return;
    }
    let [file] = event.target.files;
    if (file) {
      this.updatePhotoPreview(file);
      this.photoChanged(file);
      this.clearFileInput();
    }
    this.file.nativeElement.value = '';
  }

  updatePhotoPreview(file) {
    let reader = new FileReader();
    reader.onload = readerEvent => {
      let imageData = (readerEvent.target as any).result;
      this.photo = imageData;
    };

    reader.readAsDataURL(file);
  }

  // Control Value Accessor methods
  public writeValue(data: any) {
    let version = environment.API_VERSION;
    let url = environment.API_BASE_URL;
    if (data && data.id) {
      this.photo = `${url}/${version}/files/${data.id}/download${
        this.queryString ? '?' + this.queryString : ''
      }`;
    } else {
      this.photo = null;
    }
  }

  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  public registerOnTouched() {
    //lint
  }

  private onChange(event) {
    this.propagateChange(event);
  }

  private propagateChange = (_: any) => null;
}
